import { Stack } from "@altimateai/lego";
import { formatBytes, formatCost } from "@/helpers/utils";
import { useAppState } from "@/modules/app/useAppContext";
import { formatDatabricksCompute, formatDuration } from "./utils";
import type { QueryDetail } from "../types";
import dayjs from "dayjs";

const formatPartitions = (
  read: number | null | undefined,
  total: number | null | undefined
): string => {
  if (read === null || read === undefined) return "-";
  if (total === null || total === undefined) return `${read}`;
  const pct = total > 0 ? ((read / total) * 100).toFixed(1) : "0";
  return `${read}/${total} (${pct}%)`;
};

interface QueryMetricsGridProps {
  query: QueryDetail;
}

const QueryMetricsGrid = ({ query }: QueryMetricsGridProps) => {
  const { currency } = useAppState();

  const metric = (label: string, value: string, className?: string) => (
    <Stack direction="row" gap="xxs" className={`al-text-sm ${className ?? ""}`}>
      <dt className="al-font-semibold">{label}:</dt>
      <dd>{value}</dd>
    </Stack>
  );

  return (
    <dl>
      <Stack direction="row" className="al-flex-wrap al-gap-x-6 al-gap-y-1 al-py-2">
        {metric("Est. Cost", formatCost(query.estimated_cost_usd ?? 0, currency))}
        {metric("Exec. time", formatDuration(query.total_duration_ms))}
        {metric(
          "Partitions Scanned",
          formatPartitions(query.read_partitions, query.total_partitions)
        )}
        {metric("Query Type", query.statement_type || "-")}
        {/* Routed through the same ``formatDatabricksCompute`` helper
            as the list page's Compute column so the two surfaces
            can't drift. The backend's ``compute_display`` is the
            canonical source; ``formatDatabricksCompute`` falls back
            to the local cascade for transitional payloads. */}
        {metric("Compute", formatDatabricksCompute(query))}
        {metric("Data Read", formatBytes(query.read_bytes || 0))}
        {metric("Bytes spilled", `${formatBytes(query.spilled_local_bytes || 0)} (local)`)}
      </Stack>
      <Stack direction="row" className="al-flex-wrap al-gap-x-6 al-gap-y-1">
        {metric("User", query.user_name || "-")}
        {/* Workspace surfaced here because the column moved off the
            unified queries table — the workspace_id is still useful for
            disambiguating queries that share the same statement_text
            across tenants. ``workspace_name`` falls back to the id when
            the joined name isn't available (cluster-backed rows). */}
        {metric("Workspace", query.workspace_name || query.workspace_id || "-")}
        {metric(
          "Timestamp",
          query.start_time ? dayjs(query.start_time).format("YYYY-MM-DD HH:mm:ss") : "-"
        )}
        {metric("Cache fetched", query.resultFetchedFromCache ? "Yes" : "No")}
        {metric("Warehouse Size", query.warehouse_size || "-")}
        {query.query_hash && metric("Query Hash", query.query_hash)}
        {query.error_message &&
          metric("Execution Status", query.error_message, "al-text-amber-700")}
      </Stack>
    </dl>
  );
};

export { QueryMetricsGrid };
